import updateAllObjectsSrcToBase64 from "@/utils/helpers/updateAllObjectsSrcToBase64";
import isUUID from "@/utils/helpers/isUUID";
import { switchPixelsColor, hexToRgb } from "@/utils/helpers/switchPixelsColor";
import {
  getCardDesign,
  saveCardDesign,
  batchSaveDesigns,
  getTemplate,
} from "@/services/api";
import { trackEvent } from "@/utils/tracking";

/**
 * Design Management Mixin
 * Handles design operations: save, download, import, export, templates
 */
export default {
  methods: {
    normalizeItextSelectable(data) {
      data.objects.forEach((obj) => {
        if (obj.type?.toLowerCase().includes("text")) {
          obj.selectable = true;
        }
      });
    },

    async saveDesign(goToNextProduct = true) {
      if (this.isEditOrder) {
        this.productsVisible = true;
      }
      this.$refs.loader.show(this.$t("savingDesign"));

      let data = {
        front: window.frontCanvas.toObject([ "id", "selectable" ]),
        back: window.backCanvas.toObject([ "id", "selectable" ]),
        id: this.jobID,
        type: this.config.type,
        variant: this.config.variant,
      };

      // Filter out objects with id="default-chip"
      data.front.objects = data.front.objects.filter(obj => obj.id !== "default-chip");
      data.back.objects = data.back.objects.filter(obj => obj.id !== "default-chip");

      this.normalizeItextSelectable(data.front);
      await updateAllObjectsSrcToBase64([
        ...data.front.objects,
        data.front.backgroundImage ? data.front.backgroundImage : {},
      ]);

      this.normalizeItextSelectable(data.back);
      await updateAllObjectsSrcToBase64([
        ...data.back.objects,
        data.back.backgroundImage ? data.back.backgroundImage : {},
      ]);

      // Add metadata if it exists (productId, variantId)
      if (this.metadata) {
        data.metadata = this.metadata;
      }

      try {
        const result = await saveCardDesign(data);
        this.jobID = result.id;

        // Update notificationSent flag if present in response
        if (result.notificationSent !== undefined) {
          this.notificationSent = result.notificationSent;
        }

        // Don't change URL in addToCart mode or editOrder mode
        if (!this.isEditOrder && this.editorMode !== 'addToCart') {
          let newUrl = `/${this.jobID}`;
          window.history.replaceState(null, null, newUrl);
        }

        if (this.isEditOrder) {
          const design = window.frontCanvas.toDataURL({
            format: "png",
            multiplier: 5,
            enableRetinaScaling: true,
          });
          window?.activeCanvas?.discardActiveObject();
          this.$refs.products.updateDesign(design);

          if (goToNextProduct) {
            this.$refs.products.selectNextProduct();
          }
        } else {
          // DesignSavedModal: legacy usecase - COMMENTED OUT for micro app flow
          // if (this.editorMode !== 'addToCart') {
          //   this.$refs.designSavedModal.open();
          // }
        }

        trackEvent({
          eventName: "DesignCompleted",
          eventType: "Submit",
          action: "DesignSaved",
          details: { designId: result.id },
        });

        this.$refs.loader.hide();
      } catch (error) {
        this.$refs.loader.hide();
        trackEvent({
          eventName: "DesignCompleted",
          eventType: "Custom",
          action: "SaveFailed",
        });
        this.$refs.toast.show({
          message: this.$t("saveJobFailedMessage"),
          type: "error",
        });
      }
    },

    async downloadPNG(config) {
      trackEvent({
        eventName: "Customizer",
        eventType: "Click",
        action: "Download",
        details: { format: "png" },
      });
      this.scaleCanvas(5);
      let frontColor = window.frontCanvas.backgroundColor;
      let backColor = window.backCanvas.backgroundColor;

      // Save original visibility states
      const originalStates = {};
      
      // Apply layer visibility states if provided
      if (config.layerVisibilityStates) {
        if (window.frontCanvas) {
          window.frontCanvas.getObjects().forEach((obj, index) => {
            const objKey = `front-${index}`;
            originalStates[objKey] = obj.visible;
            if (config.layerVisibilityStates[objKey] !== undefined) {
              obj.visible = config.layerVisibilityStates[objKey];
            }
          });
        }
        
        if (window.backCanvas) {
          window.backCanvas.getObjects().forEach((obj, index) => {
            const objKey = `back-${index}`;
            originalStates[objKey] = obj.visible;
            if (config.layerVisibilityStates[objKey] !== undefined) {
              obj.visible = config.layerVisibilityStates[objKey];
            }
          });
        }
      }

      if (config.transparentCanvas) {
        window.frontCanvas.backgroundColor = "transparent";
        window.backCanvas.backgroundColor = "transparent";
      }

      await window?.frontCanvas?.discardActiveObject();
      await window?.frontCanvas?.renderAll();
      await window?.backCanvas?.discardActiveObject();
      await window?.backCanvas?.renderAll();

      if (config.from && config.to) {
        const fromRBG = hexToRgb(config.from);
        const toRGB = hexToRgb(config.to);
        switchPixelsColor(window.frontCanvas.contextContainer.canvas, fromRBG, toRGB);
        switchPixelsColor(window.backCanvas.contextContainer.canvas, fromRBG, toRGB);
      }

      const fileElement = document.createElement("a");

      let imgData = window.frontCanvas.contextContainer.canvas.toDataURL({
        quality: 1,
      });

      fileElement.href = imgData;
      fileElement.download = "front" + (this.jobID ? "-" + this.jobID : "") + ".png";
      document.body.appendChild(fileElement);
      fileElement.click();

      let imgData2 = window.backCanvas.contextContainer.canvas.toDataURL({
        format: "png",
        multiplier: 5,
        enableRetinaScaling: true,
      });

      fileElement.href = imgData2;
      fileElement.download = "back" + (this.jobID ? "-" + this.jobID : "") + ".png";
      fileElement.click();
      fileElement.parentElement.removeChild(fileElement);

      // Restore original visibility states
      if (config.layerVisibilityStates) {
        if (window.frontCanvas) {
          window.frontCanvas.getObjects().forEach((obj, index) => {
            const objKey = `front-${index}`;
            if (originalStates[objKey] !== undefined) {
              obj.visible = originalStates[objKey];
            }
          });
        }
        
        if (window.backCanvas) {
          window.backCanvas.getObjects().forEach((obj, index) => {
            const objKey = `back-${index}`;
            if (originalStates[objKey] !== undefined) {
              obj.visible = originalStates[objKey];
            }
          });
        }
      }

      window.frontCanvas.backgroundColor = frontColor;
      window.backCanvas.backgroundColor = backColor;

      this.scaleCanvas(1);

      window.frontCanvas.renderAll();
      window.backCanvas?.renderAll();
    },

    async downloadPDF(config) {
      trackEvent({
        eventName: "Customizer",
        eventType: "Click",
        action: "Download",
        details: { format: "pdf" },
      });
      // Access jsPDF from global window object (loaded via CDN)
      if (!window.jspdf || !window.jspdf.jsPDF) {
        console.error('jsPDF not loaded');
        return;
      }

      const { jsPDF } = window.jspdf;

      // Use lower scale for PDF to reduce file size
      this.scaleCanvas(5);

      // Save original visibility states
      const originalStates = {};
      
      // Apply layer visibility states if provided
      if (config.layerVisibilityStates) {
        if (window.frontCanvas) {
          window.frontCanvas.getObjects().forEach((obj, index) => {
            const objKey = `front-${index}`;
            originalStates[objKey] = obj.visible;
            if (config.layerVisibilityStates[objKey] !== undefined) {
              obj.visible = config.layerVisibilityStates[objKey];
            }
          });
        }
        
        if (window.backCanvas) {
          window.backCanvas.getObjects().forEach((obj, index) => {
            const objKey = `back-${index}`;
            originalStates[objKey] = obj.visible;
            if (config.layerVisibilityStates[objKey] !== undefined) {
              obj.visible = config.layerVisibilityStates[objKey];
            }
          });
        }
      }

      await window?.frontCanvas?.discardActiveObject();
      await window?.frontCanvas?.renderAll();
      await window?.backCanvas?.discardActiveObject();
      await window?.backCanvas?.renderAll();

      // Get canvas data
      const frontCanvas = window.frontCanvas.contextContainer.canvas;
      const backCanvas = window.backCanvas.contextContainer.canvas;

      // Standard credit card dimensions in mm
      const pdfWidth = 86;
      const pdfHeight = 55;

      // Create PDF with credit card dimensions
      const pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: [pdfWidth, pdfHeight]
      });

      // Add front page using JPEG for smaller file size
      const frontImgData = frontCanvas.toDataURL('image/jpeg', 0.92);
      pdf.addImage(frontImgData, 'JPEG', 0, 0, pdfWidth, pdfHeight);

      // Add back page
      pdf.addPage([pdfWidth, pdfHeight], 'landscape');
      const backImgData = backCanvas.toDataURL('image/jpeg', 0.92);
      pdf.addImage(backImgData, 'JPEG', 0, 0, pdfWidth, pdfHeight);

      // Save PDF
      pdf.save('design' + (this.jobID ? '-' + this.jobID : '') + '.pdf');

      // Restore original visibility states
      if (config.layerVisibilityStates) {
        if (window.frontCanvas) {
          window.frontCanvas.getObjects().forEach((obj, index) => {
            const objKey = `front-${index}`;
            if (originalStates[objKey] !== undefined) {
              obj.visible = originalStates[objKey];
            }
          });
        }
        
        if (window.backCanvas) {
          window.backCanvas.getObjects().forEach((obj, index) => {
            const objKey = `back-${index}`;
            if (originalStates[objKey] !== undefined) {
              obj.visible = originalStates[objKey];
            }
          });
        }
      }

      this.scaleCanvas(1);

      window.frontCanvas.renderAll();
      window.backCanvas?.renderAll();
    },

    duplicateDesign() {
      const sourceDesignId = this.jobID;
      trackEvent({
        eventName: "Customizer",
        eventType: "Click",
        action: "DuplicateDesign",
        details: sourceDesignId ? { sourceDesignId } : undefined,
      });
      this.jobID = null;
      this.saveDesign();
    },

    copyUrl() {
      navigator.clipboard.writeText(location.href);
    },

    extractId(url) {
      let [ baseUrl ] = url.split("?");
      let segments = baseUrl.split("/");
      return segments.pop();
    },

    async handleImport() {
      if (!this.importURL) {
        this.$refs.toast.show({
          message: this.$t("invalidDesignID"),
          type: "error",
        });
        return;
      }

      let designID = this.importURL;

      if (
        this.importURL.includes("designer.solflare.com") ||
        this.importURL.includes("designer-dev.solflare.com")
      ) {
        designID = this.extractId(this.importURL);
      }

      if (!isUUID(designID)) {
        this.$refs.toast.show({
          message: this.$t("invalidDesignID"),
          type: "error",
        });
        return;
      }

      await this.importDesign(designID);
      this.importURL = "";
      this.showImporting = false;
    },

    async importDesign(id) {
      this.$refs.loader.show(this.$t("savingDesign"));

      try {
        const data = await batchSaveDesigns([ this.jobID ], id);
        const newDesign = await getCardDesign(id);

        if (this.isEditOrder) {
          await window.frontCanvas.loadFromJSON(newDesign.front);
          if (newDesign.back && window.backCanvas) {
            await window.backCanvas.loadFromJSON(newDesign.back);
          }

          window.frontCanvas.renderAll();
          if (window.backCanvas) {
            window.backCanvas.renderAll();
          }

          const design = window.frontCanvas.toDataURL({
            format: "png",
            multiplier: 5,
            enableRetinaScaling: true,
          });
          this.$refs.products.updateDesign(design);
        }

        setTimeout(() => {
          this.$refs.loader.hide();
        }, 1000);

        setTimeout(() => {
          this.saveDesign(false);
        }, 1500);

        trackEvent({
          eventName: "Customizer",
          eventType: "Submit",
          action: "ImportDesign",
          details: { sourceDesignId: id },
        });
      } catch (error) {
        this.$refs.loader.hide();
        this.$refs.toast.show({
          message: this.$t("saveJobFailedMessage"),
          type: "error",
        });
      }
    },

    async applyTemplate({ id }) {
      trackEvent({
        eventName: "Customizer",
        eventType: "Click",
        action: "SelectTemplate",
        details: { templateId: id },
      });
      window?.activeCanvas?.discardActiveObject();
      this.showFrontSide();

      const template = await getTemplate(id, "print");
      const templateConfig = template.config;

      await window.frontCanvas.dispose();
      await window.helperCanvas.dispose();
      if (window.backCanvas?.upperCanvasEl) {
        await window.backCanvas.dispose();
      }

      await this.initializeCanvases(true, templateConfig);
      this.undoFrontHasStates = true;
    },
  },
};
